/* ============================================================
   brellie "BUY MORE & SAVE MORE" buy-boxes — clean reimplementation
   of the original (Shrine) <quantity-breaks> web component.
   - tier radios set the add-to-cart quantity
   - per-unit <select>s resolve to real variants (greys out unavailable)
   - mixed-color multipacks add as SEPARATE line items via /cart/add.js
   - tier prices/compare recompute live when the base variant changes
   Zero Shrine dependency. Fails safe: never blocks native add-to-cart.
   ============================================================ */
(function () {
  function money(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var fmt = format || '${{amount}}';
    function withCommas(n) {
      return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }
    function fixed(value, precision) {
      var n = (value / 100).toFixed(precision);
      var parts = n.split('.');
      return withCommas(parts[0]) + (parts[1] ? '.' + parts[1] : '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return fixed(cents, 2);
        case 'amount_no_decimals': return fixed(cents, 0);
        case 'amount_with_comma_separator': return fixed(cents, 2).replace(/\./g, '#').replace(/,/g, '.').replace(/#/g, ',');
        case 'amount_no_decimals_with_comma_separator': return fixed(cents, 0).replace(/,/g, '.');
        case 'amount_with_space_separator': return fixed(cents, 2).replace(/,/g, ' ');
        case 'amount_no_decimals_with_space_separator': return fixed(cents, 0).replace(/,/g, ' ');
        default: return fixed(cents, 2);
      }
    });
  }

  function rootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      try {
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.updatePrices = this.dataset.updatePrices === 'true';
        this.updatePdpPrices = this.dataset.updatePdpPrices === 'true';
        this.sectionId = this.dataset.section;

        var json = this.querySelector('script[type="application/json"]');
        this.variants = json ? JSON.parse(json.textContent) : [];

        this.form = document.getElementById('product-form-' + this.sectionId)
          || this.closest('.product__info-container')?.querySelector('form[action*="/cart/add"]')
          || document.querySelector('form[action*="/cart/add"]');

        this.radios = Array.from(this.querySelectorAll('input[type="radio"][name="quantity"]'));
        this.cards = Array.from(this.querySelectorAll('.quantity-break'));

        this.initSelects();
        this.bindVariantWatch();
        this.bindAddToCart();
        this.refreshPrices(); // sync to current base on load
      } catch (e) {
        /* never let the buy-box break the page */
        console.warn('[quantity-breaks] init failed, falling back to native form', e);
      }
    }

    /* --- current base variant (from the product form's hidden id input) --- */
    currentVariant() {
      var idInput = this.form && this.form.querySelector('input[name="id"]');
      var id = idInput ? parseInt(idInput.value, 10) : null;
      var v = id && this.variants.find(function (x) { return x.id === id; });
      return v || this.variants.find(function (x) { return x.available; }) || this.variants[0];
    }

    /* --- recompute every tier's price/compare/savings from the base price --- */
    refreshPrices() {
      if (!this.updatePrices && !this.updatePdpPrices) return;
      var base = this.currentVariant();
      if (!base) return;
      var self = this;
      this.cards.forEach(function (card) {
        var qty = parseInt(card.dataset.quantity, 10) || 1;
        var pl = parseFloat(card.dataset.percentageLeft);
        if (isNaN(pl)) pl = 1;
        var fixedOff = parseFloat(card.dataset.fixedDiscount) || 0;
        var compareMode = card.dataset.compareMode;

        var unitCompare = base.price;
        if (compareMode === 'compare_price' && base.compare_at_price && base.compare_at_price > base.price) {
          unitCompare = base.compare_at_price;
        }
        var price = Math.round(base.price * qty * pl - fixedOff);
        var compare = unitCompare * qty;
        var saved = compare - price;

        self.setText(card, '.quantity-break__price', money(price, self.moneyFormat));
        self.setText(card, '.quantity-break__compare-price', compare > price ? money(compare, self.moneyFormat) : '');

        var caption = card.querySelector('.quantity-break__caption');
        if (caption && caption.dataset.text != null) {
          var savedRounded = Math.round(saved / 100) * 100;
          caption.textContent = caption.dataset.text
            .replace(/\[amount_saved\]/g, money(saved, self.moneyFormat))
            .replace(/\[amount_saved_rounded\]/g, money(savedRounded, self.moneyFormat));
        }
      });
    }

    setText(card, sel, value) {
      var el = card.querySelector(sel);
      if (el) el.textContent = value;
    }

    /* --- when the main variant picker changes, recompute prices --- */
    bindVariantWatch() {
      if (!this.form) return;
      var self = this;
      this.form.addEventListener('change', function () {
        // let Dawn update the hidden id input first
        setTimeout(function () { self.refreshPrices(); self.refreshSelectAvailability(); }, 0);
      });
    }

    /* --- per-unit <select>s: bind change + initial availability --- */
    initSelects() {
      var self = this;
      this.querySelectorAll('.quantity-break__variant-select').forEach(function (sel) {
        sel.addEventListener('change', function () { self.refreshSelectAvailability(); });
      });
      this.refreshSelectAvailability();
    }

    /* grey out / disable option values that don't exist or are sold out,
       given the other selects within the same per-unit item */
    refreshSelectAvailability() {
      var self = this;
      this.querySelectorAll('.quantity-break__selector-item').forEach(function (item) {
        var selects = Array.from(item.querySelectorAll('.quantity-break__variant-select'));
        var chosen = selects.map(function (s) { return s.value; });
        selects.forEach(function (sel, idx) {
          Array.from(sel.options).forEach(function (opt) {
            var trial = chosen.slice();
            trial[idx] = opt.value;
            var v = self.matchVariant(trial);
            opt.classList.remove('non-existent', 'unavailable');
            if (!v) opt.classList.add('non-existent');
            else if (!v.available) opt.classList.add('unavailable');
          });
        });
      });
    }

    /* find a variant by an ordered list of option values */
    matchVariant(values) {
      return this.variants.find(function (v) {
        return values.every(function (val, i) {
          return val == null || v['option' + (i + 1)] === val;
        });
      });
    }

    selectedCard() {
      var checked = this.querySelector('input[type="radio"][name="quantity"]:checked');
      if (!checked) return null;
      return checked.nextElementSibling && checked.nextElementSibling.classList.contains('quantity-break')
        ? checked.nextElementSibling
        : this.querySelector('label[for="' + checked.id + '"]');
    }

    /* --- add-to-cart: intercept only when the selected tier has per-unit
       selectors (mixed-color multipack). Otherwise native qty-N submit. --- */
    bindAddToCart() {
      if (!this.form) return;
      var self = this;
      this.form.addEventListener('submit', function (e) {
        try {
          var card = self.selectedCard();
          if (!card) return; // native
          var items = self.buildItems(card);
          if (!items) return; // no per-unit selectors -> native qty submit
          e.preventDefault();
          e.stopImmediatePropagation();
          self.addItems(items, e.submitter || self.form.querySelector('[type="submit"]'));
        } catch (err) {
          console.warn('[quantity-breaks] ATC intercept failed, using native submit', err);
        }
      }, true);
    }

    /* build the multi-line item list for the selected tier, or null if this
       tier has no per-unit selectors (let the native form handle it) */
    buildItems(card) {
      var itemEls = Array.from(card.querySelectorAll('.quantity-break__selector-item'));
      if (!itemEls.length) return null;
      var counts = {};
      var self = this;
      for (var i = 0; i < itemEls.length; i++) {
        var selects = Array.from(itemEls[i].querySelectorAll('.quantity-break__variant-select'));
        var values = selects.map(function (s) { return s.value; });
        var v = self.matchVariant(values);
        if (!v || !v.available) return null; // bail to native if anything unresolved
        counts[v.id] = (counts[v.id] || 0) + 1;
      }
      return Object.keys(counts).map(function (id) {
        return { id: parseInt(id, 10), quantity: counts[id] };
      });
    }

    async addItems(items, button) {
      var root = rootPath();
      var drawer = document.querySelector('cart-drawer');
      if (button) button.setAttribute('aria-disabled', 'true'), button.classList.add('loading');
      var body = { items: items };
      if (drawer) {
        body.sections = ['cart-drawer', 'cart-icon-bubble'];
        body.sections_url = window.location.pathname;
      }
      try {
        var res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        var data = await res.json();
        if (data.status) { // error (e.g. sold out)
          if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('loading'); }
          alert(data.description || data.message || 'Could not add to cart.');
          return;
        }
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          if (typeof drawer.renderContents === 'function') drawer.renderContents(data);
          if (typeof drawer.open === 'function') drawer.open();
        } else {
          window.location = root + 'cart';
        }
      } catch (err) {
        window.location = root + 'cart';
      } finally {
        if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('loading'); }
      }
    }
  }

  if (!customElements.get('quantity-breaks')) {
    customElements.define('quantity-breaks', QuantityBreaks);
  }
})();
